/**
 * VerifyEmailNudge — one uniform prompt, everywhere. Shown only to signed-in
 * users who can't yet ADD to THE WALL: email accounts that never confirmed
 * their address, and anonymous accounts without a confirmed recovery email.
 * Listening, the HOARD and Gems never need this.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link, useLocation } from "react-router-dom";
import { ShieldCheck, X } from "lucide-react";
import { API, useAuth } from "../App";

const HIDE_KEY = "wall_verify_nudge_hidden";
export const needsVerification = (u) => !!u && u.role !== "admin" && (u.is_anonymous ? !u.recovery_email_verified : u.email_verified === false);

export const VerifyEmailNudge = () => {
  const { user, checkAuth } = useAuth();
  const location = useLocation();
  const [hidden, setHidden] = useState(() => sessionStorage.getItem(HIDE_KEY) === "1");
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [devCode, setDevCode] = useState("");

  useEffect(() => {
    const onNeed = () => { setHidden(false); setOpen(true); sessionStorage.removeItem(HIDE_KEY); };
    window.addEventListener("wall:verify-required", onNeed);
    return () => window.removeEventListener("wall:verify-required", onNeed);
  }, []);

  if (!needsVerification(user) || location.pathname.startsWith("/auth") || location.pathname.startsWith("/shop")) return null;
  if (hidden && !open) return null;

  const resend = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/auth/resend-code`, {}, { withCredentials: true });
      if (r.data.sent) toast.success(`Code sent to ${user.email}`);
      else if (r.data.dev_code) { setDevCode(r.data.dev_code); toast.message("Email isn't wired up yet — your code is shown below."); }
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't send a code"); }
    setBusy(false);
  };
  const verify = async () => {
    if (code.trim().length < 6) return;
    setBusy(true);
    try {
      await axios.post(`${API}/auth/verify-code?user_id=${user.user_id}&code=${code.trim()}`);
      toast.success("Verified — you can add to THE WALL now");
      await checkAuth();
      setOpen(false);
    } catch (e) { toast.error(e.response?.data?.detail || "That code didn't match"); }
    setBusy(false);
  };

  return (
    <div className="sticky top-0 z-[60] bg-[#FFF7E6] border-b-2 border-amber-300 text-[#1A1A1A]" data-testid="verify-email-nudge">
      <div className="max-w-7xl mx-auto px-4 py-2 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
        <span className="inline-flex items-center gap-2 font-bold"><ShieldCheck className="h-4 w-4 text-amber-600" /> {user.is_anonymous ? (user.access_key_for ? "Your access key opens your StarGate — confirm an email to edit your page." : "Adding to THE WALL needs a verified email.") : "Verify your email to add to THE WALL."}</span>
        <span className="text-[#5C5C5C] hidden sm:inline">Listening, your HOARD and Gems work without it — verification is only for posting businesses, bands, orgs, events, music and library resources.</span>
        {user.is_anonymous ? (
          <Link to="/dashboard" className="font-bold text-[#1E3A8A] underline underline-offset-2" data-testid="verify-nudge-recovery-link">{user.access_key_for ? "Unlock editing on your StarGate →" : "Add a recovery email in Account settings →"}</Link>
        ) : open ? (
          <span className="inline-flex items-center gap-2" data-testid="verify-nudge-form">
            <input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} inputMode="numeric" placeholder="6-digit code" className="h-8 w-28 rounded-md border border-amber-300 bg-white px-2 font-mono tracking-widest" data-testid="verify-nudge-code" />
            <button type="button" onClick={verify} disabled={busy || code.length < 6} className="h-8 rounded-full bg-[#1A1A1A] text-white px-3 font-bold disabled:opacity-40" data-testid="verify-nudge-submit">Verify</button>
            <button type="button" onClick={resend} disabled={busy} className="h-8 rounded-full border border-[#1A1A1A]/30 px-3 font-semibold" data-testid="verify-nudge-resend">Send code</button>
            {devCode && <span className="font-mono text-xs bg-white rounded px-2 py-1 border" data-testid="verify-nudge-dev-code">code: {devCode}</span>}
          </span>
        ) : (
          <button type="button" onClick={() => { setOpen(true); resend(); }} className="h-8 rounded-full bg-[#1A1A1A] text-white px-4 font-bold" data-testid="verify-nudge-open">Verify now</button>
        )}
        <button type="button" onClick={() => { setHidden(true); setOpen(false); sessionStorage.setItem(HIDE_KEY, "1"); }} className="ml-auto p-1 rounded hover:bg-black/5" aria-label="Hide for now" data-testid="verify-nudge-hide"><X className="h-4 w-4" /></button>
      </div>
    </div>
  );
};
